import React, { useEffect, useState, Fragment } from "react"
import { useStoreState, StateMapper } from "easy-peasy"
import { Link, useHistory } from "react-router-dom"
import { Button, Tooltip, message, Modal, Divider, Select, AutoComplete, Form, Row, Col, Input, Radio } from "antd"
import {
  CheckCircleTwoTone,
  CloseCircleOutlined,
  LoadingOutlined,
  PlusSquareTwoTone,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons"
import styled from "styled-components"
import moment, { Moment } from "moment-timezone"
import { FaLock } from "react-icons/fa"
import { AppDataStore } from "../appData/types"
import {
  formatArrayAsText,
  gen,
  swimminglyApi,
  useScreenSize,
  ISwimmerWithNumericGender,
  IUser,
  ISwimmerWithGuardian,
  ISwimmerClubSeason,
  IClubSeason,
  IClubSeasonInfo,
  GenderFormatter,
  DateFormats,
} from "./utils"
import DashboardLayout from "./DashboardLayout"
import ContentPanel from "./ContentPanel"
import { screenSizes } from "../styles/GlobalStyles"
import { ConfirmClubModal } from "./ConfirmClubModal"
import { SeasonHelper } from "./ClubSeasonManagement/Season"
import { RegistrationForm } from "./RegistrationForm"
import { IRegistrationClubDue, IWaiver } from "./ClubSeasonManagement/sharedState"
import { GuardianWaiverResponse, IRegistrationFormInfoResult, RegistrationHelper } from "./ClubSeasonManagement/RegistrationConfiguration/RegistrationHelper"
import { GuardianWaiverMonitor, GuardianWaiversListener } from "./Helpers/GuardianWaiverMonitor"
import { validateEmail } from "./utils"
import { AddSwimmerModal, MergeSwimmersModal } from "./ClubSwimmerRegistration/Modals"

interface IClubSwimmerRegistrationProps {
  token?: string | null
}

const StyledRegistration = styled.div`
  width: 500px;
  max-width: 95%;
  min-width: 250px;
  margin: auto;
  text-align: center;
  color: var(--darkgrey);

  h1 {
    text-align: left;
    border-bottom: 1px solid var(--darkgrey);
    padding-bottom: 5px;
  }

  .upcoming-seasons h3 {
    color: var(--black);
    margin-bottom: 0;
    font-size: 1.75rem;
  }

  .upcoming-seasons .upcoming-seasons-title {
    background: var(--mediumgrey);
    color: var(--lightgrey);
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  .upcoming-seasons .upcoming-seasons-list {
    padding-top: 10px;
    border: 1px solid var(--mediumgrey);
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  /* top-level season container: consistent padding/margins for enabled + disabled */
  .season-container {
    width: 100%;
    box-sizing: border-box;
    margin: 6px 0;
    border-radius: 6px;
    overflow: visible;
    padding: 8px; /* uniform padding for both states */
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center; /* center inner rows like season-left */
    transition: background-color 0.2s ease, box-shadow 0.2s ease,
      transform 0.2s ease;
    cursor: pointer; /* default: open/interactive */
  }

  /* fill both rows with amber when closed/queued (visual only) */
  .season-container.season-row-closed {
    background: rgba(255, 243, 205, 0.98);
    cursor: not-allowed;
  }

  /* ensure closed seasons show not-allowed cursor everywhere inside */
  .season-container.season-row-closed * {
    cursor: not-allowed !important;
  }

  /* subtle hover emphasis for open seasons */
  .season-container:not(.season-row-closed):hover {
    background: rgba(16, 18, 66, 0.03);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  /* Row B: info / countdown — centered block, left-aligned contents inside */
  .season-info-row {
    display: flex;
    align-items: center;
    justify-content: center; /* center the info row inside container */
    padding: 8px 6px;
    width: 100%;
    box-sizing: border-box;
  }

  /* badge/content in info row */
  .season-closed-note {
    font-size: 1.05rem;
  }
  .season-closed-note.inline-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0px 0px;
    background: transparent;
    border-radius: 6px;
    font-style: normal;
    margin: 0;
  }
  .season-closed-note .opens-on {
    font-size: 1.05rem;
    font-weight: 600;
    color: #5f3a00;
  }
  .season-closed-note .countdown {
    font-weight: 700;
    color: #7a3d00;
    font-size: 1.35rem;
    line-height: 1;
  }

  /* note for currently open seasons */
  .season-open-note {
    font-size: 1.05rem; /* match season-closed-note */
    font-weight: 600;
    color: #389e0d; /* pleasant green */
  }

  /* left block containing radio + label */
  .season-left {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: flex-start; /* keep radio+label left inside the centered block */
  }  
  
  /* enlarge radio visually (doubled) and keep vertical alignment */
  .season-left input[type="radio"] {
    margin: 0;
    padding: 0;
    transform: scale(1.25); /* 25% larger */
    transform-origin: center;
  }

  /* ensure season name text aligns nicely with enlarged radio */
  .season-left .season-name {
    display: inline-flex;
    align-items: center;
  }

  /* show pointer only on interactive controls for open seasons */
  .season-left input[type="radio"],
  .season-left .season-name {
    cursor: pointer;
  }

  /* for closed/not-yet-open seasons, show not-allowed on those controls */
  .season-container.season-row-closed .season-left input[type="radio"],
  .season-container.season-row-closed .season-left .season-name {
    cursor: not-allowed;
  }

  /* lock icon next to disabled radio */
  .season-lock {
    color: #6b5840;
    margin-left: 6px;
    font-size: 1.15rem;
  }   

 /* season name */
  .season-left .season-name {
    margin-left: 8px;
    font-size: 1.55rem;
    font-weight: 900;
    color: #101242;
  } 

  .merge-swimmer-footnote {
    max-width: 400px;
    line-height: 0.2;
    margin: auto;
    margin-top: 15px;
  }

  @media (max-width: ${screenSizes.small}px) {
    width: 95%;

    .radio-options {
      margin-bottom: 8px;
    }

.season-container.season-row-closed {
      padding: 8px 6px;
    }
    .season-info-row {
      padding-left: 16px;
      flex-direction: column;
      align-items: stretch;
    }
    .season-closed-note.inline-badge {
      justify-content: space-between;
      width: 100%;
    }

    .merge-swimmer-footnote {
      line-height: 1;
    }

    .merge-swimmer-footnote button {
      height: 15px;
    }
  }

 .swimmer-name-input {
    color: var(--black); /* or any color: "#222", "red", etc. */
  }
  `

const MySwimmersTable = styled.table`
  /* border-collapse: separate; */
  /* width: 80%; */
  margin: auto;

  td,
  th {
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
  }

  tr {
    -webkit-transition: background-color 1s ease;
    -moz-transition: background-color 1s ease;
    -o-transition: background-color 1s ease;
    transition: background-color 1s ease;

    -webkit-transition: color 1s ease;
    -moz-transition: color 1s ease;
    -o-transition: color 1s ease;
    transition: color 1s ease;
  }

  tr:nth-child(even) {
    background-color: var(--tableblue);
  }
  tr:nth-child(odd) {
    background-color: var(--white);
  }
  tr.newly-registered {
    background-color: var(--approvalgreen);
    color: var(--white);
  }
`
const waiverMonitor = new GuardianWaiverMonitor()
//keith says: keep the state of the waiver responses outside of the react component which re-renders more often than i expect
const allWaiverResponses: Record<string, GuardianWaiverResponse> = {}

export default function ClubSwimmerRegistration({
  token,
}: IClubSwimmerRegistrationProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const [clubId, setClubId] = useState<number | undefined>()
  const [clubName, setClubName] = useState("")
  const [seasons, setSeasons] = useState<IClubSeason[]>([])
  const [seasonsNotYetOpen, setSeasonsNotYetOpen] = useState<IClubSeason[]>([])
  const [usersSwimmers, setUsersSwimmers] = useState<ISwimmerWithGuardian[]>([])
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null)
  const [selectedClubSeasonInfo, setSelectedClubSeasonInfo] = useState<IClubSeasonInfo | undefined>(undefined)
  const [pricePoints, setPricePricePoints] = useState<IRegistrationClubDue[]>([])
  const [waivers, setWaivers] = useState<IWaiver[]>([])
  const [waiverResponses, setWaiverResponses] = useState<Record<string, GuardianWaiverResponse>>(allWaiverResponses)
  const [waiversCompleted, setWaiversCompleted] = useState<boolean>(false)
  const [selectedSwimmers, setSelectedSwimmers] = useState<number[]>([])
  const [mergeSwimmersVisible, setMergeSwimmersVisible] = useState(false)
  const [swimmersToMerge, setSwimmersToMerge] = useState<number[]>([])
  const [mergeFirstName, setMergeFirstName] = useState("")
  const [mergeMiddleInitial, setMergeMiddleInitial] = useState("")
  const [mergeLastName, setMergeLastName] = useState("")
  const [mergeGender, setMergeGender] = useState<"Boy" | "Girl" | "">("")
  const [mergeBirthdate, setMergeBirthdate] = useState<Moment | null>(null)
  const [mergeElligible, setMergeElligible] = useState(false)
  const [confirmMerge, setConfirmMerge] = useState(false)
  const [addSwimmerModalVisible, setAddSwimmerModalVisible] = useState(false)
  const [addSwimmerFirstName, setAddSwimmerFirstName] = useState("")
  const [addSwimmerMiddleInitial, setAddSwimmerMiddleInitial] = useState("")
  const [addSwimmerLastName, setAddSwimmerLastName] = useState("")
  const [addSwimmerGender, setAddSwimmerGender] =
    useState<"Boy" | "Girl" | "">("")
  const [addSwimmerBirthdate, setAddSwimmerBirthdate] =
    useState<Moment | null>(null)
  const [addElligible, setAddElligible] = useState(false)
  const [triggerUsersSwimmerRefresh, setTriggerUsersSwimmerRefresh] =
    useState(0)
  const [newestRegisteredSwimmer, setNewestRegisteredSwimmer] =
    useState<number | null>(null)
  const [newlyRegisteredSwimmers, setNewlyRegisteredSwimmers] = useState<
    number[]
  >([])
  const [confirmSignUpModalVisible, setConfirmSignUpModalVisible] =
    useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingClubInformation, setLoadingClubInformation] = useState(false)
    const [clubConfirmationModalVisible, setClubConfirmationModalVisible] =
    useState(false)
  const [seasonSelectionForModal, setSeasonSelectionForModal] = useState(-1)
  const [clubCardRequiredModalVisible, setClubCardRequiredModalVisible] = useState(false)
    // shared "now" clock used for all countdowns (updates once per second)
  const [now, setNow] = useState<Moment>(moment())
  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 1000)
    return () => clearInterval(t)
  }, [])

    // reset helpers for Add modal
  const resetAddModalState = () => {
    setAddSwimmerFirstName("")
    setAddSwimmerMiddleInitial("")
    setAddSwimmerLastName("")
    setAddSwimmerGender("")
    setAddSwimmerBirthdate(null)
  }

  const openAddModal = () => {
    resetAddModalState()
    setAddSwimmerModalVisible(true)
  }

  // helper to format remaining ms -> "Xd HH:MM:SS" or "HH:MM:SS"
  const formatRemaining = (ms: number) => {
    if (ms <= 0) return "00:00:00"
    let sec = Math.floor(ms / 1000)
    const days = Math.floor(sec / (24 * 3600))
    sec -= days * 24 * 3600
    const hours = Math.floor(sec / 3600)
    sec -= hours * 3600
    const minutes = Math.floor(sec / 60)
    const seconds = sec - minutes * 60
    const hh = String(hours).padStart(2, "0")
    const mm = String(minutes).padStart(2, "0")
    const ss = String(seconds).padStart(2, "0")
    return days > 0 ? `${days}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`
  }

  const isMobile = ["small", "medium"].includes(useScreenSize())
  const history = useHistory()
  const [addGuardianModalVisible, setAddGuardianModalVisible] = useState(false)
  const [newGuardianEmail, setNewGuardianEmail] = useState("")
  const [modalContent, setModalContent] = useState<ISwimmerWithGuardian | null>()

  useEffect(() => {
    setSwimmersToMerge([])
    setMergeFirstName("")
    setMergeMiddleInitial("")
    setMergeLastName("")
    setMergeGender("")
    setMergeBirthdate(null)
    setConfirmMerge(false)
  }, [mergeSwimmersVisible])

  useEffect(() => {
    setAddSwimmerFirstName("")
    setAddSwimmerMiddleInitial("")
    setAddSwimmerLastName("")
    setAddSwimmerGender("")
    setAddSwimmerBirthdate(null)
  }, [addSwimmerModalVisible])

  useEffect(() => {
    if (
      swimmersToMerge.length > 1 &&
      mergeFirstName.trim() !== "" &&
      mergeLastName.trim() !== "" &&
      mergeGender !== "" &&
      mergeBirthdate !== null
    ) {
      setMergeElligible(true)
    } else {
      setMergeElligible(false)
    }
  }, [
    swimmersToMerge,
    mergeFirstName,
    mergeLastName,
    mergeGender,
    mergeBirthdate,
  ])

  useEffect(() => {
    if (
      addSwimmerFirstName.trim() !== "" &&
      addSwimmerLastName.trim() !== "" &&
      addSwimmerGender !== "" &&
      addSwimmerBirthdate !== null
    ) {
      setAddElligible(true)
    } else {
      setAddElligible(false)
    }
  }, [
    addSwimmerFirstName,
    addSwimmerLastName,
    addSwimmerGender,
    addSwimmerBirthdate,
  ])

  useEffect(() => {
    let mounted = true
    if (token && token.trim() !== "") {
      setLoadingClubInformation(true)
      swimminglyApi.get(gen(`/api/getClubInformationFromToken/${token}`)).then(
        ({
          status,
          club,
        }: {
          status: string
          club?: {
            clubId: number
            name: string
            nameLong: string
            seasons: IClubSeason[]
          }
        }) => {
          setLoadingClubInformation(false)
          if (club) {
            if (mounted) {
            const validSeasons = club.seasons.filter((clubSeason) => {
              // registration explicitly disabled
              if (clubSeason.isRegistrationEnabled !== 1) {
                return false
              }
              // require a signUpsOpenDate and ensure it is not in the future
              if (!clubSeason.signUpsOpenDate) return false

              const tz = (clubSeason as any).timezone
              const openMoment = tz
                ? moment.tz(clubSeason.signUpsOpenDate, tz)
                : moment(clubSeason.signUpsOpenDate)

              // invalid dates should exclude the season
              if (!openMoment.isValid()) return false

              // compare by startOf('day') to avoid time-of-day/timezone off-by-one
              if (openMoment.clone().startOf("day").isAfter(moment().startOf("day")))
                return false

              return true
            })
              const invalidSeasons = club.seasons.filter(
                (clubSeason) =>
                  !validSeasons
                    .map((s) => s.seasonId)
                    .includes(clubSeason.seasonId),
              )

              console.log("Club fetch - raw seasons:", club.seasons)
              console.log(
                "Club fetch - validSeasons:",
                validSeasons.map((s) => ({
                  seasonId: s.seasonId,
                  seasonName: s.seasonName,
                  signUpsOpenDate: s.signUpsOpenDate,
                  timezone: (s as any).timezone,
                  isRegistrationEnabled: s.isRegistrationEnabled,
                  defaultPaymentMethodId: s.defaultPaymentMethodId,
                  clubPays: s.clubPays,
                })),
              )
              console.log(
                "Club fetch - invalidSeasons:",
                invalidSeasons.map((s) => ({
                  seasonId: s.seasonId,
                  seasonName: s.seasonName,
                  signUpsOpenDate: s.signUpsOpenDate,
                  timezone: (s as any).timezone,
                })),
              )
              


              setClubName(club.nameLong.trim())
              setSeasons(validSeasons)
              setSeasonsNotYetOpen(invalidSeasons)
              setClubId(club.clubId)
            }
          }
        },
      )
    }
    return () => {
      mounted = false
    }
  }, [token])

  useEffect(() => {
    let mounted = true
    console.log("ClubSwimmerRegistration: token:", token)
    if (user?.userId) {
      swimminglyApi
        .get(gen(`/api/getAllSwimmersOfGuardian/${user.userId}`))
        .then(
          ({
            status,
            swimmers,
          }: {
            status: string
            swimmers: {
              swimmer: ISwimmerWithNumericGender
              swimmerShoulderNumber: number
              thisGuardianStatus: "confirmed" | "requested"
              guardians: {
                user: IUser
                guardianStatus: "confirmed" | "requested"
              }[]
              clubSeasons: ISwimmerClubSeason[]
            }[]
          }) => {
            if (status === "success" && swimmers) {
              if (selectedSwimmers.length > 0) {
                // compare selectedSwimmers with newSelectedSwimmers
                const newSelectedSwimmers = selectedSwimmers
                  .filter((currentlySelectedSwimmer) =>
                    swimmers
                      .map((newSwimmer) => newSwimmer.swimmer.swimmerId)
                      .includes(currentlySelectedSwimmer),
                  )
                  .sort((a, b) => (a > b ? 1 : -1))
                let shouldUpdate = false
                if (newSelectedSwimmers.length === selectedSwimmers.length) {
                  for (let i = 0; i < selectedSwimmers.length; i++) {
                    if (newSelectedSwimmers[i] !== selectedSwimmers[i])
                      shouldUpdate = true
                  }
                } else {
                  shouldUpdate = true
                }
                if (mounted && shouldUpdate)
                  setSelectedSwimmers(newSelectedSwimmers)
              }
              const newUsersSwimmers: ISwimmerWithGuardian[] = []
              for (let i = 0; i < swimmers.length; i++) {
                newUsersSwimmers.push({
                  swimmer: swimmers[i].swimmer,
                  guardians: swimmers[i].guardians,
                  thisGuardianStatus: swimmers[i].thisGuardianStatus,
                  swimmerShoulderNumber: swimmers[i].swimmerShoulderNumber,
                  clubSeasons: swimmers[i].clubSeasons,
                })
              }
              if (mounted) {
                setUsersSwimmers(newUsersSwimmers)
              }
            } else {
              message.error("Something went wrong retrieving swimmers")
            }
          },
        )
    }
    return () => {
      mounted = false
    }
  }, [user?.userId, selectedSwimmers, triggerUsersSwimmerRefresh])

  useEffect(() => {
    let mounted = true
    if (mounted && newestRegisteredSwimmer !== null)
      setNewlyRegisteredSwimmers([
        ...newlyRegisteredSwimmers,
        newestRegisteredSwimmer,
      ])
    setNewestRegisteredSwimmer(null)
    return () => {
      mounted = false
    }
  }, [newestRegisteredSwimmer, newlyRegisteredSwimmers])


  const registrationHelper = new RegistrationHelper()
  const seasonHelper = new SeasonHelper()

  /**
   * Safely updates the state of waiverResponses as well as allWaiverResponses
   * @param guardianWaiverResponse 
   */
  const updateGuardianWaiverResponses = (guardianWaiverResponse: GuardianWaiverResponse[]) => {
    const waiverResponsesCopy = Object.assign({}, waiverResponses)
    guardianWaiverResponse.forEach((guardianWaiverResponse) => {
      waiverResponsesCopy[guardianWaiverResponse.waiverSectionId] = guardianWaiverResponse
      allWaiverResponses[guardianWaiverResponse.waiverSectionId] = guardianWaiverResponse
    })

    setWaiverResponses(waiverResponsesCopy)
  }

  //Club Registration data
  useEffect(() => {
    if (clubId !== undefined && selectedSeason !== undefined) {
      seasonHelper.getClubSeason(clubId, seasonSelectionForModal)
        .then(async (clubSeasonInfo: IClubSeasonInfo) => {
          setSelectedClubSeasonInfo(clubSeasonInfo)

          if (!clubSeasonInfo.usingRegistration) {
            return Promise.resolve(undefined)
          } else {
            //we only get the registration form information if this is a registration club
            return registrationHelper.getRegistrationFormInfo(clubId, seasonSelectionForModal, user?.userId)
              .then((registrationFormInfoResult: IRegistrationFormInfoResult) => {
                setPricePricePoints(registrationFormInfoResult.registrationForm.pricePoints)
                setWaivers(registrationFormInfoResult.registrationForm.waivers)
                updateGuardianWaiverResponses(Object.values(registrationFormInfoResult.guardianWaiverResponsesByWaiverSectionId))

                const waiverResponsesValues = Object.values(registrationFormInfoResult.guardianWaiverResponsesByWaiverSectionId)
                const registrationFormId = registrationFormInfoResult.registrationForm.registrationInfo.registrationFormId

                const waiverMonitorListener: GuardianWaiversListener = ((waiverSectionId: number | undefined, guardianAcknowledged: boolean | undefined, freeformResponse: string | undefined, isCompleted: boolean) => {

                  if (waiverSectionId !== undefined && user !== null) {
                    registrationHelper.setGuardianWaiverResponse(user.userId, registrationFormId, waiverSectionId, guardianAcknowledged, freeformResponse)
                    const guardianWaiverResponse = { waiverSectionId: waiverSectionId.toString(), guardianAcknowledged: guardianAcknowledged, freeformResponse: freeformResponse }
                    updateGuardianWaiverResponses([guardianWaiverResponse])
                  } else {
                    //enable the registration button
                    setWaiversCompleted(isCompleted)
                  }
                });

                waiverMonitor.init(registrationFormInfoResult.registrationForm.waivers, waiverResponsesValues, [waiverMonitorListener])
              })
              .catch((err) => {
                console.log(err)
                message.error("No season available")
              })

          }
        })
        .catch((err) => {
          console.log(err)
          message.error("No season available")
        })
    }

  }, [selectedSeason])

  useEffect(() => {
    if (selectedClubSeasonInfo?.usingRegistration) {
      setWaiversCompleted(waiverMonitor.isCompleted)
    } else {
      setWaiversCompleted(true)
    }
  }, [selectedSwimmers])

  /**********************
   * no club && no user *
   **********************/
  if ((!clubName || clubName.trim() === "") && !user) {
    return (
      <DashboardLayout user={user}>
        <header className="page-header">
          <h2>Sign Up Swimmers</h2>
        </header>
        <div>
          <p>
            Uh oh, we couldn't find the club you're looking for. Are you sure
            you navigated to the right link?
          </p>
          <p>
            If you're trying to add your children to the roster for a swim
            season please contact the administrator from the club you're trying
            to sign up for to make sure you've gone to the right link.
          </p>
        </div>
      </DashboardLayout>
    )
  }

  /*******************
   * no club && user *
   *******************/
  if ((!clubName || clubName.trim() === "") && user) {
    return (
      <DashboardLayout user={user}>
        <header className="page-header">
          <h2>Sign Up Swimmers</h2>
        </header>
        <div>
          {loadingClubInformation 
            ? (<LoadingOutlined hidden={false} spin={true} />)
            : (
              <ContentPanel title={"No Club Found"}>
                <p>
                  Uh oh, we couldn't find the club you're looking for. Are you sure
                  you navigated to the right link?
                </p>
                <p>
                  If you're trying to add your children to the roster for a swim
                  season please contact the administrator from the club you're
                  trying to sign up for to make sure you've gone to the right link.
                </p>
              </ContentPanel>
              ) }
        </div>
      </DashboardLayout>
    )
  }

  /*******************
   * club && no user *
   *******************/
  if (clubName && clubName.trim() !== "" && !user) {
    return (
      <DashboardLayout user={user}>
        <header className="page-header">
          <h2>Registration: {clubName}</h2>
        </header>
        <div>
          <ContentPanel>
            <p>
              Hi there! It looks like you're not signed in to the Swimmingly
              Clubhouse. You'll need to{" "}
              <Link to={{
                pathname: '/registerUser',
                state: { clubToken: token }
              }}>create an account</Link> or{" "}
              <Link to={{
                pathname: '/login',
                state: { token }
              }}>sign in</Link> to add your children to a season
              for {clubName}.
            </p>
          </ContentPanel>
        </div>
      </DashboardLayout>
    )
  }
  const validSeasonIds = new Set(seasons.map((season) => {
    return season.seasonId
  }))

  return (
    <DashboardLayout user={user}>
      <header className="page-header">
        <h2>Registration: {clubName}</h2>
      </header>
      <div>
        <ContentPanel>
          <StyledRegistration>
          <p>
              Register new/returning swimmers for upcoming season for<br />{" "}
              <b
                style={{
                  backgroundColor: "green",
                  color: "white",
                  padding: "2px",
                }}
              >
                {clubName}
              </b>
            </p>
            <div className="upcoming-seasons">
              <div className="upcoming-seasons-title">
                <h3>Upcoming Seasons</h3>
              </div>
              <div className="upcoming-seasons-list">
                {
                  // destruct the arrays to create one array of seasons and seasonsNotYetOpen
                  [...seasons, ...seasonsNotYetOpen].map((theSeason, index) => {
                    // timezone if present on the season object (some seasons may not include it)
                    const tz = (theSeason as any).timezone
                    const hasSignUpsDate = Boolean(theSeason.signUpsOpenDate)
                    const openMoment = hasSignUpsDate
                      ? tz
                        ? moment.tz(theSeason.signUpsOpenDate, tz)
                        : moment(theSeason.signUpsOpenDate)
                      : null

                    // whether the configured open date is still in the future (based on shared `now`)
                    const isFuture = openMoment ? openMoment.isAfter(now) : false
                    const isRegistrationClosed =
                      theSeason.isRegistrationEnabled !== 1
                    const isMissingClubDefaultCard =
                      Boolean(theSeason.clubPays && theSeason.clubPays > 0) &&
                      !theSeason.defaultPaymentMethodId
                    const opensLater = hasSignUpsDate && isFuture

                    // seasons are interactive only when registration is enabled and the open date has arrived
                    const isValidSeasonDynamic =
                      !isRegistrationClosed &&
                      (validSeasonIds.has(theSeason.seasonId) ||
                        (hasSignUpsDate && !isFuture))
                    const isSelectableSeason =
                      isValidSeasonDynamic && !isMissingClubDefaultCard

                    const opensFull = openMoment ? openMoment.format("dddd, MMMM D, YYYY") : ""
                    const remainingMs = openMoment ? openMoment.diff(now) : 0

                    return (
                      <div
                        className={`season-container ${!isValidSeasonDynamic ? "season-row-closed" : ""}`}
                        key={`season_fragment_${theSeason.seasonId}_${index}`}
                        onClick={() => {
                          if (!isSelectableSeason) return
                          setSeasonSelectionForModal(theSeason.seasonId)
                          setClubConfirmationModalVisible(true)
                        }}
                      >
                        {/* Row A: selector (radio + season name) */}
                      <div className="season-left">
                            <input
                              type="radio"
                              id={`season_option_${index}`}
                              name="season"
                              // disabled if season not yet valid
                              disabled={!isSelectableSeason}
                              checked={selectedSeason === theSeason.seasonId}
                              value={theSeason.seasonId}
                              key={`${theSeason.seasonId}`}
                              onChange={() => {
                                if (!isSelectableSeason) return
                                // keep handler for direct radio changes (e.g., keyboard navigation)
                                setSeasonSelectionForModal(theSeason.seasonId)
                                setClubConfirmationModalVisible(true)
                              }}
                            />
                            <div className="season-name">
                              {theSeason.seasonName}
                            </div>
                            {/* show lock icon for disabled seasons */}
                            {!isSelectableSeason && (
                              <FaLock className="season-lock" aria-hidden="true" />
                            )}
                          </div>

                        {/* Row B: registration message + countdown (left aligned, full width of container) */}
                        <div className="season-info-row" aria-live="polite">
                          {isMissingClubDefaultCard && (
                            <div className="season-closed-note inline-badge" role="status">
                              <InfoCircleOutlined style={{ fontSize: "1.25rem", color: "#d48806" }} />
                              <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                <div className="opens-on">Club payment card required before registration can begin.</div>
                              </div>
                              <Button
                                type="text"
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  setClubCardRequiredModalVisible(true)
                                }}
                                aria-label={`Why ${theSeason.seasonName} registration is unavailable`}
                              >
                                Learn more
                              </Button>
                            </div>
                          )}

                          {!isValidSeasonDynamic && !isRegistrationClosed && opensLater && (
                            <div className="season-closed-note inline-badge" role="status">
                              <ClockCircleOutlined style={{ fontSize: "1.25rem", color: "#d48806" }} />
                              <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                <div className="opens-on">Registration opens on {opensFull}</div>
                                {/* when remainingMs <= 0 this will be replaced by "Registration Open" */}
                                {remainingMs > 0 ? (
                                  <div className="countdown">Opens in {formatRemaining(remainingMs)}</div>
                                ) : (
                                  <div className="countdown">Registration Open</div>
                                )}
                              </div>
                            </div>
                          )}

                          {!isMissingClubDefaultCard && !isValidSeasonDynamic && isRegistrationClosed && (
                            <div className="season-closed-note">
                              Registration for this season is currently closed.
                            </div>
                          )}

                          {/* fallback note for disabled seasons without a configured date */}
                          {!isMissingClubDefaultCard && !isValidSeasonDynamic && !isRegistrationClosed && (!hasSignUpsDate || !isFuture) && (
                            <div className="season-closed-note">Registration for this season is not currently open.</div>
                          )}

                          {/* note for currently open seasons */}
                          {isSelectableSeason && (
                            <div className="season-open-note">Registration for this season is currently open.</div>
                          )}
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            </div>
            {selectedSeason && usersSwimmers.length === 0 && (
              <p style={{ marginTop: "20px" }}>
                You don't have any swimmers in the
                Swimmingly Clubhouse! Add your swimmers now to register them for any season for {clubName}!
              </p>
            )}
            {selectedSeason && usersSwimmers.length > 0 && (
              <>
                <p style={{ marginTop: "20px" }}>
                  Select swimmers for season registration:{" "}<br />
                  <b
                    style={{
                      background: "var(--primaryblue)",
                      color: "var(--white)",
                      padding: "2px",
                      fontSize: "10px",
                    }}
                  >
                    Then click 'Proceed to Register' at the bottom of the screen.
                  </b>
                </p>
                
                  <MySwimmersTable>
                    <thead>
                      <tr>
                        <Tooltip title="Season registration status of swimmer">
                          <th>Status</th>
                        </Tooltip>
                        <th>Name</th>
                        <th>Sex</th>
                        <th>Birthdate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersSwimmers.map((theSwimmer, index) => {
                        const thisSeason = theSwimmer.clubSeasons?.find(
                          (cS) => cS.seasonId === selectedSeason,
                        )
                        return (
                          <tr
                            key={`swimmer_${theSwimmer.swimmer.swimmerId}_row_${index}`}
                            className={
                              newlyRegisteredSwimmers.includes(
                                theSwimmer.swimmer.swimmerId,
                              )
                                ? "newly-registered"
                                : "normal"
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedSwimmers.includes(
                                  theSwimmer.swimmer.swimmerId,
                                )}
                                disabled={thisSeason?.registered === 1 || false}
                                onChange={() => null}
                                onClick={() => {
                                  setModalContent(theSwimmer)
                                  setAddGuardianModalVisible(true)
                                  setNewGuardianEmail("")
                                  if (
                                    selectedSwimmers.includes(
                                      theSwimmer.swimmer.swimmerId,
                                    )
                                  ) {
                                    setSelectedSwimmers(
                                      selectedSwimmers.filter(
                                        (s) =>
                                          s !== theSwimmer.swimmer.swimmerId,
                                      ),
                                    )
                                  } else {
                                    setSelectedSwimmers([
                                      ...selectedSwimmers,
                                      theSwimmer.swimmer.swimmerId,
                                    ])
                                  }
                                }}
                              />
                              {" "}
                              {thisSeason?.registered ? (
                                  <Tooltip title="Registered">
                                    <CheckCircleTwoTone
                                      style={{ fontSize: "1.5rem" }}
                                      twoToneColor="#52c41a"
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Not Registered">
                                    <CloseCircleOutlined
                                      style={{ fontSize: "1.5rem" }}
                                    />
                                  </Tooltip>
                                )}
                            </td>
                            <td>{`${theSwimmer.swimmer.firstName} ${theSwimmer.swimmer.lastName}`}</td>
                            <td>
                              <Tooltip title="Sex">
                              {GenderFormatter.format(`${theSwimmer.swimmer.gender}`)}
                              </Tooltip>
                            </td>
                            <td>
                              <Tooltip title="Date of Birth">
                                {moment(theSwimmer.swimmer.dateOfBirth).format(
                                  DateFormats.monthDayYear,
                                )}
                              </Tooltip>
                            </td>
                          </tr>

                        )

                      })}
                    </tbody>
                  </MySwimmersTable>
  
                <Modal
                  title={`Invite User as a Guardian for ${modalContent?.swimmer.firstName} ${modalContent?.swimmer.lastName}`}
                  open={addGuardianModalVisible}
                  okText="Add Guardian"
                  cancelText="No More Guardians"
                  onOk={() => {
                    const guardianEmail = newGuardianEmail.trim().toLowerCase()
                    if (
                      modalContent?.swimmer.swimmerId &&
                      validateEmail(newGuardianEmail.trim().toLowerCase())
                    ) {
                      registrationHelper.addGuardianToSwimmer(
                        modalContent?.swimmer.swimmerId,
                        user?.userId,
                        guardianEmail,
                      )
                      setAddGuardianModalVisible(false)
                      setModalContent(null)
                      setNewGuardianEmail("")
                    } else {
                      message.warning("Please check the email you are trying to add...", 4)
                    }
                  }}
                  onCancel={() => {
                    setAddGuardianModalVisible(false)
                    setModalContent(null)
                    setNewGuardianEmail("")
                  }}
                >
                  <p>
                    Enter email of any guardian you would like to tag to this
                    swimmer. 
                    <br /><br />
                    If guardian does not
                    have an account, they will receive an email invite to create their account.
                  </p>
                  <input
                    value={newGuardianEmail}
                    onChange={(e) =>
                      setNewGuardianEmail(e.target.value.trim().toLowerCase())
                    }
                    placeholder="email..."
                  />
                </Modal>

                {usersSwimmers.length > 1 ? (
                  <p className="merge-swimmer-footnote">
                    Duplicate swimmers? Contact Swimmingly to merge them.
                  </p>
                ) : null}
              </>
            )}
            <Divider />
            {selectedSeason && (
              <Fragment>
                <h5
                  style={{ marginTop: "0px" }}
                  onClick={() => openAddModal()}
                >
                  Need to Add a New Swimmer?
                </h5>
                <PlusSquareTwoTone
                  style={{ fontSize: "2.5rem" }}
                  twoToneColor="#52c41a"
                  onClick={() => openAddModal()}
                />
                <br />
              </Fragment>
            )}
            <Divider />

            {selectedSeason && selectedClubSeasonInfo?.usingRegistration && (
              <Fragment>
                <h4
                  style={{ marginTop: "25px" }}
                >
                  You are about to register your child{`${selectedSwimmers.length > 1 ? "(ren) " : " "}`} for {clubName} {selectedClubSeasonInfo.seasonName}.
                </h4>
                <RegistrationForm
                  previewMode={false}
                  selectedClubSeason={selectedClubSeasonInfo}
                  pricePoints={pricePoints}
                  waivers={waivers}
                  waiverResponses={waiverResponses}
                  waiverMonitor={waiverMonitor}
                  children={undefined}
                // tbd waiver responses
                />
                <br />
              </Fragment>
            )}
            {selectedSeason && (
              <Button
                type="primary"
                disabled={selectedSwimmers.length === 0 || !waiversCompleted}
                style={{ marginTop: "35px", marginBottom: "20px" }}
                onClick={() => setConfirmSignUpModalVisible(true)}
              >
                Proceed to Register
              </Button>
            )}
            <hr />

            {/* Replace inline Add modal with AddSwimmerModal */}
            <AddSwimmerModal
              visible={addSwimmerModalVisible}
              onClose={() => {
                setAddSwimmerModalVisible(false)
                setTriggerUsersSwimmerRefresh(triggerUsersSwimmerRefresh + 1)
              }}
              onAdd={async (vals) => {
                await swimminglyApi
                  .post(gen("/api/createNewSwimmer"))
                  .body({
                    firstName: vals.firstName,
                    middleInitial: vals.middleInitial,
                    lastName: vals.lastName,
                    gender: vals.gender,
                    birthday: vals.birthdate?.format("YYYY-MM-DD"),
                    createdBy: user?.userId,
                  })
                  .then((response) => {
                    if (response.status === "error") {
                      message.error("Uh oh, something went wrong...")
                    } else if (response.status === "success") {
                      message.success("Success!")
                    }
                  })
                  .catch((err) => {
                    console.log(err)
                    message.error("Uh oh, something went wrong...")
                  })
                  .finally(() => {
                    setNewestRegisteredSwimmer(null)
                    setNewlyRegisteredSwimmers([])
                    setTriggerUsersSwimmerRefresh(triggerUsersSwimmerRefresh + 1)
                  })
              }}
              initialValues={{
                firstName: addSwimmerFirstName,
                middleInitial: addSwimmerMiddleInitial,
                lastName: addSwimmerLastName,
                gender: addSwimmerGender === "" ? undefined : addSwimmerGender,
                birthdate: addSwimmerBirthdate,
              }}
            />

          </StyledRegistration>
        </ContentPanel>

        {/* Replace inline Merge modal with MergeSwimmersModal */}
        <MergeSwimmersModal
          visible={mergeSwimmersVisible}
          onClose={() => setMergeSwimmersVisible(false)}
          swimmersToMerge={swimmersToMerge}
          usersSwimmers={usersSwimmers}
          onMerge={async (payload) => {
            await swimminglyApi
              .post(gen("/api/mergeMultipleSwimmersIntoOne"))
              .body({
                swimmerIds: payload.swimmerIds,
                firstName: payload.firstName,
                middleInitial: payload.middleInitial,
                lastName: payload.lastName,
                gender: payload.gender,
                dateOfBirth: payload.birthdate?.format("YYYY-MM-DD"),
                userId: user?.userId,
              })
              .then((results) => {
                if (results?.status === "success") {
                  message.success("Success!")
                  setTriggerUsersSwimmerRefresh(triggerUsersSwimmerRefresh + 1)
                } else {
                  message.error("Uh oh, something went wrong...")
                }
              })
              .catch((err) => {
                console.log(err)
                message.error("Uh oh, something went wrong..")
                setTriggerUsersSwimmerRefresh(triggerUsersSwimmerRefresh + 1)
              })
          }}
        />

        <Modal
          title="Club Card Required"
          open={clubCardRequiredModalVisible}
          centered
          onCancel={() => setClubCardRequiredModalVisible(false)}
          footer={[
            <Button
              key="close-club-card-required"
              type="primary"
              onClick={() => setClubCardRequiredModalVisible(false)}
            >
              Close
            </Button>,
          ]}
        >
          <p>
            This club cannot accept season registration yet because no club payment card is on file.
          </p>
          <p style={{ marginBottom: 0 }}>
            Please contact your club administrator and try again once a club card has been added.
          </p>
        </Modal>

        <Modal
          title="Proceed with Registration?"
          open={confirmSignUpModalVisible}
          centered
          destroyOnHidden
          mask={{ closable: false }}
          onCancel={() => setConfirmSignUpModalVisible(false)}
          footer={[
            <Button
              key="close-confirm-sign-up"
              onClick={() => {
                setConfirmSignUpModalVisible(false)
              }}
            >
              Close
            </Button>,
            <Button
              key="confirm-swimmer-sign-up"
              type="primary"
              loading={loading}
              onClick={async () => {
                setLoading(true)
                setTriggerUsersSwimmerRefresh(triggerUsersSwimmerRefresh + 1)
                setSelectedSwimmers([])
                setConfirmSignUpModalVisible(false)
                await new Promise((resolve) => setTimeout(resolve, 1500))
                setLoading(false)
                setNewestRegisteredSwimmer(null)
                setNewlyRegisteredSwimmers([])
                history.push("/app/cart", {
                  clubId: clubId,
                  seasonId: selectedClubSeasonInfo?.seasonId,
                  swimmerIds: selectedSwimmers,
                  usingRegistration: selectedClubSeasonInfo?.usingRegistration,
                  selectedClubSeasonInfo: selectedClubSeasonInfo,
                })
              }}
            >
              Proceed to Register
            </Button>,
          ]}
        >
          <p>
            Proceed to register the following swimmers for the club/season:
            <br />
            <b>{`${clubName} | ${selectedClubSeasonInfo?.seasonName ?? "season"}`}</b>
            <br />
            <br />
            {formatArrayAsText(
              selectedSwimmers.map((s) => {
                const theSwimmer = usersSwimmers.find(
                  (uS) => uS.swimmer.swimmerId === s,
                )
                return theSwimmer ? `${theSwimmer.swimmer.firstName} ${theSwimmer.swimmer.lastName}` : ""
              }),
            )}
          </p>
        </Modal>
        <ConfirmClubModal
          clubName={clubName}
          visible={clubConfirmationModalVisible}
          setVisible={setClubConfirmationModalVisible}
          onOk={() => {
            if (seasonSelectionForModal > -1) {
              setSelectedSeason(seasonSelectionForModal)
            } else {
              message.warning("Can't determine what season you're selecting")
            }
          }}
        />
      </div>
    </DashboardLayout>
  )
}